import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Select,
  Card,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { TextArea } = Input;

// Función para calcular la edad a partir de la fecha de nacimiento
const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  
  const fechaNac = dayjs(fechaNacimiento);
  const hoy = dayjs();
  const edad = hoy.diff(fechaNac, 'year');
  
  return edad >= 0 ? edad : null;
};

const SolicitudesNuevosAlumnos = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalObservacionesVisible, setModalObservacionesVisible] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar solicitudes
  const loadSolicitudes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/solicitudes-alumnos');
      if (response.ok) {
        const data = await response.json();
        setSolicitudes(data);
      } else {
        message.error('Error al cargar las solicitudes');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSolicitudes();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ver detalle de solicitud
  const handleView = (record) => {
    setSolicitudSeleccionada(record);
    setModalVisible(true);
  };

  // Actualizar estado de solicitud
  const handleUpdateEstado = async (id, nuevoEstado, observaciones = null) => {
    try {
      const dataToSend = {
        estado: nuevoEstado,
        ...(observaciones && { observaciones }),
      };

      const response = await apiClient.put(`/solicitudes-alumnos/${id}`, dataToSend);
      if (response.ok) {
        message.success(`Solicitud ${nuevoEstado.toLowerCase()} correctamente`);
        loadSolicitudes();
        setModalVisible(false);
        setModalObservacionesVisible(false);
        form.resetFields();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al actualizar la solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al actualizar la solicitud');
    }
  };

  // Abrir modal para agregar observaciones y cambiar estado
  const handleCambiarEstado = (solicitud, nuevoEstado) => {
    setSolicitudSeleccionada(solicitud);
    form.setFieldsValue({
      observaciones: solicitud.sol_observaciones || '',
    });
    setModalObservacionesVisible(true);
    // Guardar el nuevo estado en el form para usarlo después
    form.setFieldValue('nuevoEstado', nuevoEstado);
  };

  // Guardar observaciones y cambiar estado
  const handleGuardarObservaciones = async () => {
    try {
      const values = await form.validateFields();
      const nuevoEstado = form.getFieldValue('nuevoEstado');
      await handleUpdateEstado(solicitudSeleccionada.sol_id, nuevoEstado, values.observaciones);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Eliminar solicitud
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/solicitudes-alumnos/${id}`);
      if (response.ok) {
        message.success('Solicitud eliminada correctamente');
        loadSolicitudes();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la solicitud');
    }
  };

  // Abrir WhatsApp con el número de teléfono
  const handleContactarWhatsApp = (telefono) => {
    if (!telefono) {
      message.warning('No hay número de teléfono disponible');
      return;
    }

    // Limpiar el número: quitar espacios, guiones, paréntesis y el signo +
    const numeroLimpio = telefono.replace(/[\s\-\(\)\+]/g, '');
    
    // Si el número no empieza con código de país (Argentina es 54), asumimos que es local
    // Para Argentina, si empieza con 0, lo quitamos y agregamos 54
    let numeroFormateado = numeroLimpio;
    if (numeroLimpio.startsWith('0')) {
      numeroFormateado = '54' + numeroLimpio.substring(1);
    } else if (!numeroLimpio.startsWith('54') && numeroLimpio.length <= 10) {
      // Si no tiene código de país y parece ser un número local argentino, agregar 54
      numeroFormateado = '54' + numeroLimpio;
    }

    // Abrir WhatsApp
    const url = `https://wa.me/${numeroFormateado}`;
    window.open(url, '_blank');
  };

  // Obtener color del tag según estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'PENDIENTE':
        return 'orange';
      case 'APROBADA':
        return 'green';
      case 'RECHAZADA':
        return 'red';
      default:
        return 'default';
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'sol_nombre',
      key: 'sol_nombre',
    },
    {
      title: 'Apellido',
      dataIndex: 'sol_apellido',
      key: 'sol_apellido',
    },
    {
      title: 'Email',
      dataIndex: 'sol_email',
      key: 'sol_email',
      render: (email) => email || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'sol_telefono',
      key: 'sol_telefono',
      render: (telefono, record) => (
        <Space>
          <span>{telefono || '-'}</span>
          {telefono && (
            <Button
              type="link"
              icon={<MessageOutlined />}
              onClick={() => handleContactarWhatsApp(telefono)}
              style={{ 
                padding: 0, 
                height: 'auto',
                color: '#25D366'
              }}
            >
              Contactar
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_solicitud_descripcion',
      key: 'tipo_solicitud_descripcion',
    },
    {
      title: 'Estado',
      dataIndex: 'sol_estado',
      key: 'sol_estado',
      width: 120,
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado}
        </Tag>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'sol_fecha_creacion',
      key: 'sol_fecha_creacion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            Ver
          </Button>
          {record.sol_estado === 'PENDIENTE' && (
            <>
              <Button
                type="link"
                style={{ color: '#52c41a' }}
                onClick={() => handleCambiarEstado(record, 'APROBADA')}
              >
                Aprobar
              </Button>
              <Button
                type="link"
                danger
                onClick={() => handleCambiarEstado(record, 'RECHAZADA')}
              >
                Rechazar
              </Button>
            </>
          )}
          <Popconfirm
            title="¿Estás seguro de eliminar esta solicitud?"
            onConfirm={() => handleDelete(record.sol_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Solicitudes de Nuevos Alumnos</h1>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadSolicitudes}
            loading={loading}
          >
            {isMobile ? null : 'Actualizar'}
          </Button>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {solicitudes.map(solicitud => (
              <Card
                key={solicitud.sol_id}
                hoverable
                style={{
                  borderLeft: `4px solid ${getEstadoColor(solicitud.sol_estado) === 'orange' ? '#faad14' : getEstadoColor(solicitud.sol_estado) === 'green' ? '#52c41a' : '#f5222d'}`,
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {solicitud.sol_nombre} {solicitud.sol_apellido}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Email: {solicitud.sol_email || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Teléfono: {solicitud.sol_telefono || '-'}</span>
                        {solicitud.sol_telefono && (
                          <Button
                            type="link"
                            icon={<MessageOutlined />}
                            onClick={() => handleContactarWhatsApp(solicitud.sol_telefono)}
                            style={{ 
                              padding: 0, 
                              height: 'auto',
                              color: '#25D366'
                            }}
                          >
                            Contactar
                          </Button>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Categoría: {solicitud.categoria_descripcion || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Tipo: {solicitud.tipo_solicitud_descripcion}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha: {solicitud.sol_fecha_creacion ? dayjs(solicitud.sol_fecha_creacion).format('DD/MM/YYYY') : '-'}
                      </div>
                    </div>
                    <Tag color={getEstadoColor(solicitud.sol_estado)}>
                      {solicitud.sol_estado}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(solicitud)}
                    >
                      Ver
                    </Button>
                    {solicitud.sol_estado === 'PENDIENTE' && (
                      <>
                        <Button
                          type="link"
                          style={{ color: '#52c41a' }}
                          onClick={() => handleCambiarEstado(solicitud, 'APROBADA')}
                        >
                          Aprobar
                        </Button>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleCambiarEstado(solicitud, 'RECHAZADA')}
                        >
                          Rechazar
                        </Button>
                      </>
                    )}
                    <Popconfirm
                      title="¿Estás seguro de eliminar esta solicitud?"
                      onConfirm={() => handleDelete(solicitud.sol_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={solicitudes}
            loading={loading}
            rowKey="sol_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} solicitudes`,
            }}
          />
        )}

        {/* Modal para ver detalle de solicitud */}
        <Modal
          title="Detalle de Solicitud"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setSolicitudSeleccionada(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setModalVisible(false);
              setSolicitudSeleccionada(null);
            }}>
              Cerrar
            </Button>,
            solicitudSeleccionada?.sol_estado === 'PENDIENTE' && (
              <>
                <Button
                  key="approve"
                  type="primary"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleCambiarEstado(solicitudSeleccionada, 'APROBADA')}
                >
                  Aprobar
                </Button>
                <Button
                  key="reject"
                  danger
                  onClick={() => handleCambiarEstado(solicitudSeleccionada, 'RECHAZADA')}
                >
                  Rechazar
                </Button>
              </>
            ),
          ].filter(Boolean)}
          width={700}
        >
          {solicitudSeleccionada && (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Nombre">
                {solicitudSeleccionada.sol_nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Apellido">
                {solicitudSeleccionada.sol_apellido}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Nacimiento">
                {solicitudSeleccionada.sol_fecha_nacimiento 
                  ? dayjs(solicitudSeleccionada.sol_fecha_nacimiento).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Edad">
                {solicitudSeleccionada.sol_fecha_nacimiento 
                  ? `${calcularEdad(solicitudSeleccionada.sol_fecha_nacimiento) ?? '-'} años`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Sexo">
                {solicitudSeleccionada.sol_sexo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {solicitudSeleccionada.sol_email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Teléfono">
                <Space>
                  <span>{solicitudSeleccionada.sol_telefono || '-'}</span>
                  {solicitudSeleccionada.sol_telefono && (
                    <Button
                      type="link"
                      icon={<MessageOutlined />}
                      onClick={() => handleContactarWhatsApp(solicitudSeleccionada.sol_telefono)}
                      style={{ 
                        padding: 0, 
                        height: 'auto',
                        color: '#25D366'
                      }}
                    >
                      Contactar
                    </Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Categoría">
                {solicitudSeleccionada.categoria_descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Solicitud">
                {solicitudSeleccionada.tipo_solicitud_descripcion}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                <Tag color={getEstadoColor(solicitudSeleccionada.sol_estado)}>
                  {solicitudSeleccionada.sol_estado}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Creación">
                {solicitudSeleccionada.sol_fecha_creacion 
                  ? dayjs(solicitudSeleccionada.sol_fecha_creacion).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </Descriptions.Item>
              {solicitudSeleccionada.sol_observaciones && (
                <Descriptions.Item label="Observaciones">
                  {solicitudSeleccionada.sol_observaciones}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Modal>

        {/* Modal para agregar observaciones y cambiar estado */}
        <Modal
          title={`Cambiar Estado a ${form.getFieldValue('nuevoEstado') === 'APROBADA' ? 'APROBADA' : 'RECHAZADA'}`}
          open={modalObservacionesVisible}
          onOk={handleGuardarObservaciones}
          onCancel={() => {
            setModalObservacionesVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="observaciones"
              label="Observaciones (opcional)"
            >
              <TextArea
                rows={4}
                placeholder="Ingrese observaciones sobre esta solicitud..."
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SolicitudesNuevosAlumnos;

